import React, { useCallback, useMemo, useState } from 'react';
import { styled } from 'styled-components';
import { Spin } from 'antd';
import { ActionsBar, ConfigurationSelect, Title } from '@ydistri/ds';
import { RegionFlowGroupResponse } from '@ydistri/api-sdk';
import { DefaultOptionType } from 'rc-select/lib/Select';
import {
  usePostRegionToGroupMutation,
  useRemoveRegionFromGroupMutation,
  useRemoveRegionGroupMutation,
} from '../apiRegionFlowGroups';
import DeleteIconButton from '../../../../components/buttons/DeleteIconButton';
import { rtkQueryPatchStatusToConfigurationFieldStatus } from '@ydistri/utils';
import { useGetRegionsQuery } from '../../../../apis/apiLists';
import useIsConfigurationEditable from '../../../../hooks/useIsConfigurationEditable';
import { COLORS, computeRemSize } from '@ydistri/theme';
import { useTemplateOrCalculationWithValue } from '../../../../hooks/useTemplateOrCalculationWithValue';

const GroupRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  padding: 0.25rem;
  gap: 0.5rem;

  &:hover {
    background-color: ${COLORS.HOVER_GREY_LIGHT};
  }
`;

const GroupSpin = styled(Spin)`
  width: 100%;
  padding: 0.25rem;
`;

const GroupTitle = styled(Title)`
  width: ${computeRemSize(120)};
`;

interface RegionGroupSelectProps {
  regionGroup: RegionFlowGroupResponse;
}

type LatestRegionGroupAction = 'postRegionToGroup' | 'removeRegionFromGroup';

const RegionGroup: React.FC<RegionGroupSelectProps> = ({ regionGroup }) => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const isConfigurationEditable = useIsConfigurationEditable();
  const [latestAction, setLatestAction] = useState<LatestRegionGroupAction>('postRegionToGroup');
  const { data: regions } = useGetRegionsQuery(templateOrCalculation.value);
  const [postRegionToGroup, postRegionToGroupStatus] = usePostRegionToGroupMutation();
  const [removeRegionFromGroup, removeRegionFromGroupStatus] = useRemoveRegionFromGroupMutation();
  const [removeRegionGroup, removeRegionGroupStatus] = useRemoveRegionGroupMutation();

  const options: DefaultOptionType[] =
    regions?.array.map(region => ({
      value: region.id,
      label: region.name,
    })) ?? [];

  const deleteGroup = useCallback(() => {
    removeRegionGroup({
      templateId: templateOrCalculation.id,
      regionFlowGroupIds: [regionGroup.id],
    });
  }, [regionGroup.id, removeRegionGroup, templateOrCalculation.id]);

  const handleChange = useCallback(
    (value: number[]) => {
      const newRegion: number[] = [];
      const deleteRegion: number[] = [];

      value
        .filter(v => !regionGroup.regions.map(r => r.id).includes(v))
        .forEach(v => {
          newRegion.push(v);
        });

      if (newRegion.length > 0) {
        setLatestAction('postRegionToGroup');
        postRegionToGroup({
          templateId: templateOrCalculation.id,
          regionId: newRegion[0],
          regionGroupId: regionGroup.id,
        });
      }

      regionGroup.regions
        .map(r => r.id)
        .forEach(v => {
          if (!value.includes(v)) deleteRegion.push(v);
        });

      if (deleteRegion.length > 0) {
        setLatestAction('removeRegionFromGroup');
        removeRegionFromGroup({
          templateId: templateOrCalculation.id,
          regionId: deleteRegion[0],
          regionGroupId: regionGroup.id,
        });
      }
    },
    [
      postRegionToGroup,
      regionGroup.id,
      regionGroup.regions,
      removeRegionFromGroup,
      templateOrCalculation.id,
    ],
  );

  const value: number[] = useMemo(() => regionGroup.regions.map(r => r.id), [regionGroup]);

  const latestStatus =
    latestAction === 'postRegionToGroup' ? postRegionToGroupStatus : removeRegionFromGroupStatus;
  const status = rtkQueryPatchStatusToConfigurationFieldStatus(latestStatus);

  return (
    <GroupSpin key={regionGroup.id} spinning={removeRegionGroupStatus.isLoading}>
      <GroupRow>
        <GroupTitle>Group {regionGroup.id}</GroupTitle>
        <ConfigurationSelect<number[]>
          disabled={!isConfigurationEditable}
          mode="multiple"
          placeholder="Please select"
          value={value}
          onChange={handleChange}
          options={options}
          width="100%"
          status={status}
          disableOnLoading={false}
          size="large"
        />
        <ActionsBar>
          <DeleteIconButton
            size="normal"
            disabled={!isConfigurationEditable}
            tooltip={`Delete group ${regionGroup.id}`}
            onClick={deleteGroup}
            data-testid="DeleteRegionGroupButton"
          />
        </ActionsBar>
      </GroupRow>
    </GroupSpin>
  );
};

export default RegionGroup;
